'use client';
import React from 'react';

import Head from 'next/head';

const textWithEllipsis = ({ text, maxLength }) => {
  if (typeof text !== 'string') return null;
  let ellipsifiedString = '';

  const regexp = new RegExp(`^(.{0,${maxLength - 1}}[a-z])[\\W]+`, 'i');
  const match = text.match(regexp);

  if (text.length > maxLength) ellipsifiedString = match && match.length > 1 ? `${match[1]}…` : text;
  else ellipsifiedString = text;

  return ellipsifiedString;
};

const getPureText = string => {
  if (typeof string !== 'string') return null;

  const stripedHtml = string.replace(/<[^>]+>/g, '');
  // var pureString = htmlEntities.decode(stripedHtml);

  return stripedHtml;
};

const getProperTitles = title => {
  const siteBrand = 'Prusa Knowledge Base';

  const baseTitle = `${title ? `${title} | ` : ''}${siteBrand}`;

  const twitterTitle = textWithEllipsis({
    text: baseTitle,
    maxLength: 55,
  });

  return {
    base: baseTitle,
    twitter: twitterTitle,
  };
};

const getProperDescriptions = description => {
  const purifiedDescription = getPureText(description);

  const baseDescription = textWithEllipsis({
    text: purifiedDescription,
    maxLength: 140,
  });

  const ogDescription = textWithEllipsis({
    text: purifiedDescription,
    maxLength: 65,
  });

  return {
    base: baseDescription,
    og: ogDescription,
  };
};

interface SEOHeaderProps {
  description: string;
  title: string | null;
  image: { original: string } | string | null | undefined;
  keywords?: string;
  translations?: Record<string, { slug: string; translated: number }>;
}

export default function SEOHeader({
  description,
  title = null,
  image = { original: '/images/Original_Prusa_3D_printers.png' },
  keywords = '',
}: // translations = {},
SEOHeaderProps) {
  // const router = useRouter();
  // const { t } = useTranslations();
  const [getImageMeta, setImageMeta] = React.useState<{
    w: number;
    h: number;
  } | null>(null);

  // const FONT_URL_PREFIX = SHOULD_USE_CDN ? CDN_URL : '';

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const titleMemo = React.useMemo(() => getProperTitles(title), [title]);

  //   const descriptionMemo = React.useMemo(
  //     () => {
  //       getProperDescriptions(
  //         description) // || t(messages.description), { pageTitle: title }
  //       )
  // }, [description]);
  const descriptionMemo = React.useMemo(() => getProperDescriptions(description), [description]);

  // const xDefaultHref = translations?.['en']?.slug
  //   ? `${process.env.NEXT_PUBLIC_ORIGIN}${pathname.replace(
  //       /\/[^/]+?$/,
  //       `/${translations['en'].slug}`
  //     )}`
  //   : `${process.env.NEXT_PUBLIC_ORIGIN}${pathname}`;

  React.useEffect(() => {
    if (image) {
      const img = new Image();

      img.onload = () => {
        setImageMeta({ w: img.width, h: img.height });
      };

      if (typeof image === 'object') img.src = image?.original;
      else img.src = image;
    }
  }, [image]);

  return (
    <Head>
      {/* <title>{titleMemo.base}</title> */}

      {titleMemo.base ? <meta name="subject" content={titleMemo.base} /> : null}
      {/* {descriptionMemo.base ? (
        <meta name="description" content={descriptionMemo.base} />
      ) : null} */}
      {keywords ? <meta name="keywords" content={keywords} /> : null}

      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${process.env.NEXT_PUBLIC_ORIGIN}${pathname}`} />
      {titleMemo.base ? <meta property="og:title" content={titleMemo.base} /> : null}
      {typeof image === 'object' && image?.original ? <meta property="og:image" content={image.original} /> : null}
      {typeof image === 'string' ? <meta property="og:image" content={image} /> : null}
      {getImageMeta ? <meta property="og:image:width" content={`${getImageMeta.w}`} /> : null}
      {getImageMeta ? <meta property="og:image:height" content={`${getImageMeta.h}`} /> : null}
      {descriptionMemo.og ? <meta property="og:description" content={descriptionMemo.og} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Prusa3D" />
      {titleMemo.twitter ? <meta name="twitter:title" content={titleMemo.twitter} /> : null}
      {descriptionMemo.base ? <meta name="twitter:description" content={descriptionMemo.base} /> : null}
      {titleMemo.twitter ? <meta name="twitter:image:alt" content={titleMemo.twitter} /> : null}
      {typeof image === 'object' && image?.original ? <meta name="twitter:image" content={image.original} /> : null}
      {typeof image === 'string' ? <meta name="twitter:image" content={image} /> : null}

      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />

      {/* <link
        rel="preload"
        href={`${FONT_URL_PREFIX}/visuals/fonts/AtlasGrotesk-Regular.ttf`}
        as="font"
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={`${FONT_URL_PREFIX}/visuals/fonts/AtlasGrotesk-RegularItalic.ttf`}
        as="font"
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={`${FONT_URL_PREFIX}/visuals/fonts/AtlasGrotesk-Medium.ttf`}
        as="font"
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={`${FONT_URL_PREFIX}/visuals/fonts/AtlasGrotesk-Black.ttf`}
        as="font"
        crossOrigin="anonymous"
      /> */}

      {/* {
        <link
          rel="alternate"
          href={xDefaultHref}
          hrefLang="x-default"
          key="x-default"
        />
      } */}
      {/* {LOCALES.map(locale => {
        // Pages with dynamic path provide object with translations containin proper slug.
        const translation = translations?.[locale];

        if (!translation && locale === 'ja') return null; // Exclude Japanese in case of pages with static path.
        if (translation && translation?.translated != 1) return null; // Exclude not translated language mutations for dynamic paths.

        const localePath = locale === 'en' ? '' : `/${locale}`;
        const pagePath = translation
          ? pathname.replace(/\/[^/]+?$/, `/${translation?.slug}`)
          : pathname;

        return (
          <link
            rel="alternate"
            href={`${process.env.NEXT_PUBLIC_ORIGIN}${localePath}${pagePath}`}
            hrefLang={locale}
            key={`${locale}-${pagePath}`}
          />
        );
      })} */}

      <script src="/js/tinymce/tinymce.min.js" referrerPolicy="origin"></script>
    </Head>
  );
}
